
import { create } from 'zustand';
import _ from 'lodash';
import { 
    getGroup,
    saveGroup,
    updateGroup,
    deleteGroup,
    getCompanyGroups,
    createGroupV3
} from '../reducers/group/resources';

import { getCompanyContactsV3 } from '../reducers/contact/resources';


import { archiveGroupV3, deleteGroupV3, duplicateGroupV3, exportGroupV3 } from '../reducers/group/resources';

import { message } from 'antd';
import session from '../utils/session';
import mixpanel from '../utils/mixpanel';

const mockTemplates = [
    {
        id: 'job_fair',
        icon: '📢',
        name: 'Job Fair',
        description: 'Capture leads, follow up within 24 hours',
        tags: ['Job Fair'],
        longCopy: {
            headline: 'Turn booth conversations into same-day follow-ups.',
            summary: 'Use a Job Fair group to capture every lead you meet at an event and keep the momentum going before they move on to the next booth. Fast follow-up is the difference between a warm applicant and a cold one.',
            whatYouCanDo: [
                'Import leads from a CSV or sign-in sheet in seconds',
                'Send a thank-you text within 24 hours of the event',
                'Share your application link and next steps automatically',
                'Tag contacts by booth, role, or recruiter for later outreach',
            ],
            examples: [
                '"Thanks for stopping by booth 12! Here\'s the link to apply: …"',
                'Auto-reply when a lead texts back — route to the right recruiter',
                'Drip: day-1 thanks, day-3 reminder, day-7 "still interested?"',
            ],
        },
    },
    {
        id: 'hiring_event',
        icon: '🗓️',
        name: 'Hiring Event',
        description: 'Reminders, directions, check-in',
        tags: ['Hiring Event'],
        longCopy: {
            headline: 'Keep every attendee informed before and during your event.',
            summary: 'A Hiring Event group handles the logistics so you can focus on the interviews. Send confirmations, directions, and real-time updates to everyone on the invite list in one place.',
            whatYouCanDo: [
                'Send RSVP confirmations and calendar invites',
                'Share parking, directions, and what to bring the day before',
                'Post check-in links and interview assignments morning-of',
                'Follow up with no-shows and attendees separately',
            ],
            examples: [
                '"Reminder: your interview is tomorrow at 10 AM. Parking tips: …"',
                '"We\'re live! Check in here: <link>. Can\'t wait to meet you."',
                'Post-event: thank-you message with next-step links',
            ],
        },
    },
    {
        id: 'candidates',
        icon: '📝',
        name: 'Candidate Pipeline',
        description: 'Applicants by role/stage',
        tags: ['Candidates'],
        longCopy: {
            headline: 'Segment applicants by role and stage so outreach stays relevant.',
            summary: 'Group candidates by the job they applied to or the stage they\'re in, then send messages that match where they are in your process. No more one-size-fits-all blasts.',
            whatYouCanDo: [
                'Separate pipelines by role, shift, or location',
                'Move contacts between stages (Applied → Screened → Interview)',
                'Send stage-specific instructions and self-scheduling links',
                'Nurture passive candidates with periodic check-ins',
            ],
            examples: [
                '"Next steps for RN – Night Shift: book your screen here."',
                '"You\'re shortlisted! Pick a 30-min slot that works for you: <link>"',
                'Quarterly "still looking?" nudge to candidates you didn\'t hire',
            ],
        },
    },
    {
        id: 'employee_engagement',
        icon: '🎉',
        name: 'Employee Engagement',
        description: 'Announcements, surveys, RSVP',
        tags: ['Employee'],
        longCopy: {
            headline: 'Reach your current team with the stuff they\'ll actually read.',
            summary: 'Use this group for internal announcements, quick pulse surveys, and event RSVPs. Text meets people where they are — far higher open rates than email for time-sensitive updates.',
            whatYouCanDo: [
                'Announce company news, policy changes, or perks',
                'Run short pulse surveys and collect feedback fast',
                'Manage RSVPs for all-hands, town halls, and outings',
                'Send shift swaps, schedule updates, and urgent alerts',
            ],
            examples: [
                '"Friday town hall at 3 PM — reply YES to RSVP."',
                '"Quick 2-question pulse: how was this week? 😀 😐 😫"',
                '"Reminder: open enrollment closes this Friday. Details: <link>"',
            ],
        },
    },
    {
        id: 'new_hires',
        icon: '🚀',
        name: 'Ordering / New Hires',
        description: 'Day 0–30 comms, document links',
        tags: ['Onboarding'],
        longCopy: {
            headline: 'Guide new hires through their first 30 days without dropping the ball.',
            summary: 'A New Hires group automates the day-0-through-day-30 cadence that onboarding needs: welcome notes, paperwork reminders, gear orders, and early check-ins. Consistency matters — this keeps every hire on the same track.',
            whatYouCanDo: [
                'Send day-0 welcome with paperwork and IT links',
                'Remind hires to complete I-9, W-4, and direct deposit',
                'Order swag, laptops, or uniforms with a single trigger',
                'Automate day-7, day-14, and day-30 check-ins',
            ],
            examples: [
                '"Welcome to the team! Start here: <onboarding link>"',
                '"Quick check: did your laptop arrive? Reply YES/NO."',
                'Day 30: "How\'s it going? 2-min survey: <link>"',
            ],
        },
    },
    { id: 'custom', icon: '⚙️', name: 'Custom', description: 'Build from scratch', tags: ['Custom Group'] },
];

const useGroupsStore = create((set, get) => ({
    groups: [],
    loading: false,
    loadingStats: false,
    activeGroup: null,
    templates: mockTemplates,
    companyContacts: [],
    totalContacts: 0,
    
    // Filters
    searchQuery: '',
    filters: {
        purpose: 'all',
        channel: 'all',
        status: 'active'
    },

    // Wizard State
    wizardOpen: false,
    wizardStep: 0,
    wizardData: {},

    // The active company ID — for gh-admin users this is set by the company
    // selector; for regular users it falls back to their own company.
    activeCompanyId: null,
    setActiveCompanyId: (id) => set({ activeCompanyId: id }),

    // Actions
    setSearchQuery: (query) => set({ searchQuery: query }),
    setFilter: (key, value) => set((state) => ({ filters: { ...state.filters, [key]: value } })),
    
    setWizardOpen: (open) => set({ wizardOpen: open, wizardStep: 0, wizardData: {} }),
    setWizardStep: (step) => set({ wizardStep: step }),
    updateWizardData: (data) => set((state) => ({ wizardData: { ...state.wizardData, ...data } })),

    fetchGroups: async () => {
        set({ loading: true });
        try {
            const { id: currentUserId, companyId: sessionCompanyId } = session.get('userData') || {};
            const companyId = get().activeCompanyId || sessionCompanyId;

            const res = await getCompanyGroups(companyId);
            // The v3 API returns { data: { statusCode, data: [...], otherPrivateGroupCount } }
            const groupsData = res.data.data || res.data || [];

            // Filter out private groups that don't belong to the user
            const visibleGroups = groupsData.filter(g => {
                if (g.privacy === 'private' && g.user_id !== currentUserId) {
                    return false;
                }
                return true;
            });
            
            // Mock enrichment for UI demo purposes if fields are missing
            const enrichedGroups = visibleGroups.map(g => {
                const purpose = g.purpose || 'Custom';
                const template = mockTemplates.find(t => t.tags.includes(purpose)) || mockTemplates.find(t => t.id === 'custom');
                return {
                    ...g,
                    purpose,
                    icon: template?.icon || '⚙️',
                    channel: g.channel || 'SMS',
                    status: g.status || 'Active'
                };
            });

            set({ groups: enrichedGroups, loading: false });
        } catch (error) {
            console.error(error);
            set({ loading: false });
            message.error('Failed to load groups');
        }
    },

    createGroup: async (groupData) => {
        set({ loading: true });
        try {
            const { id: currentUserId } = session.get('userData') || {};
            
            const payload = {
                name: groupData.name,
                privacy: groupData.privacy || 'public',
                contacts: groupData.contactIds || []
            };
            
            mixpanel.track('group:create', { purpose: groupData.purpose, privacy: payload.privacy });

            const res = await createGroupV3(payload);
            if (res.data) {
                message.success('Group created successfully');
                get().fetchGroups(); // Refresh list
                set({ wizardOpen: false });
                return res.data;
            }
        } catch (error) {
            console.error(error);
            message.error('Failed to create group');
            throw error; // Rethrow to let caller handle navigation
        } finally {
             set({ loading: false });
        }
    },

    fetchCompanyContacts: async (companyId, params, options = {}) => {
        set({ loading: true });
        try {
            const res = await getCompanyContactsV3(companyId, params);
            const data = res.data?.data || res.data || { rows: [], total: 0 };
            
            if (options.replace) {
                 set({ 
                    companyContacts: data.rows,
                    totalContacts: data.total,
                    loading: false 
                });
            } else if (params.page > 1) {
                set((state) => ({ 
                    companyContacts: [...state.companyContacts, ...data.rows],
                    totalContacts: data.total,
                    loading: false 
                }));
            } else {
                set({ 
                    companyContacts: data.rows,
                    totalContacts: data.total,
                    loading: false 
                });
            }
            return data;
        } catch (error) {
            console.error(error);
            set({ loading: false });
            message.error('Failed to fetch contacts');
            return { rows: [], total: 0 };
        }
    },

    deleteGroup: async (groupId) => {
        try {
            await deleteGroupV3(groupId);
            message.success('Group deleted');
            get().fetchGroups();
        } catch (error) {
            console.error(error);
            message.error('Failed to delete group');
        }
    },

    archiveGroup: async (groupId) => {
        try {
            await archiveGroupV3(groupId);
            message.success('Group archived');
            get().fetchGroups();
        } catch (error) {
            console.error(error);
            message.error('Failed to archive group');
        }
    },

    duplicateGroup: async (groupId, name, privacy) => {
        try {
            const res = await duplicateGroupV3(groupId, { name, privacy });
            if (res.data) {
                message.success('Group duplicated successfully');
                get().fetchGroups();
                return res.data;
            }
        } catch (error) {
            console.error(error);
            // Check for specific error message from backend if possible, otherwise generic
            const errorMsg = error.response?.data?.message || 'Failed to duplicate group';
            message.error(errorMsg);
            throw error;
        }
    },

    exportGroup: async (group) => {
        try {
            const res = await exportGroupV3(group.id);
            console.log('Export API Response:', res);
            
            // Handle potentially nested response structure
            const url = res.data?.data?.url || res.data?.url || res.url;

            if (url) {
                const sanitizedName = group.name.replace(/[^a-z0-9]/gi, '_').toLowerCase();
                const filename = `gohire_group_${sanitizedName}_export.csv`;

                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                return true;
            } else {
                console.error('Export response missing URL:', res);
                message.error('Failed to get download URL');
            }
        } catch (error) {
            console.error(error);
            message.error('Failed to export group');
            throw error;
        }
    },

    applyGroupMembersChanged: ({ groupId, contactCount }) => {
        const { groups } = get();
        const idx = groups.findIndex(g => g.id === groupId);
        if (idx === -1) return;
        const next = [...groups];
        next[idx] = { ...next[idx], contact_count: contactCount };
        set({ groups: next });
    },

    // Helper to get filtered groups
    getFilteredGroups: () => {
        const { groups, searchQuery, filters } = get();
        return groups.filter(g => {
            const matchesSearch = g.name.toLowerCase().includes(searchQuery.toLowerCase());
            
            // Status filter
            // Active: show if NOT archived
            // Archived: show if archived
            let matchesStatus = true;
            if (filters.status === 'active') {
                matchesStatus = !g.is_archived;
            } else if (filters.status === 'archived') {
                matchesStatus = !!g.is_archived;
            }

            // Purpose filter
            const matchesPurpose = filters.purpose === 'all' || g.purpose === filters.purpose;

            return matchesSearch && matchesStatus && matchesPurpose; 
        });
    }
}));

export default useGroupsStore;
